import {Component, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {ClassificationType, IncidentSubtype, TypologyApiService} from '@npck/af-frontend-api';
import {NESTED_TAB, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {Editor} from 'ngx-editor';
import {NGX_EDITOR_DEFAULT_TOOLBAR} from '../../../../../../../../coid/src/app/infra/ngx-editor-config';
import {DESCRIPTION_MAX_LENGTH} from '../../../../../domain/constants';
import {CLASSIFICATION_SUBTYPE, CLASSIFICATION_TYPE} from '../../../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../../../layout/main-container/main-container.component';
import {Path} from '../../../../../path';
import {ClassificationDetailsTab} from '../classification-details.component';

@Component({
    selector: 'af-classification-create-edit-common-information',
    standalone: false,
    templateUrl: './classification-create-edit-common-information.component.html',
    styleUrl: './classification-create-edit-common-information.component.scss',
})
export class ClassificationCreateEditCommonInformationComponent {
    @ViewChild('mainContainer') mainContainer: MainContainerComponent;
    DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
    NGX_EDITOR_DEFAULT_TOOLBAR = NGX_EDITOR_DEFAULT_TOOLBAR;
    tabPathToTab: Record<string, ClassificationType> = {
        [ClassificationType.FRAUD_TYPE.toLowerCase()]: ClassificationType.FRAUD_TYPE,
    };
    form: FormGroup<ClassificationForm>;
    editor = new Editor();

    private type: ClassificationType;
    private subtype: string;
    private backToTab: ClassificationDetailsTab;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private typologyApiService: TypologyApiService,
        private snackbarService: SnackbarService,
        private routerService: RouterService,
    ) {
        this.type = this.activatedRoute.snapshot.paramMap.get(CLASSIFICATION_TYPE).toUpperCase() as ClassificationType;
        this.subtype = this.activatedRoute.snapshot.paramMap.get(CLASSIFICATION_SUBTYPE).toUpperCase();
        this.backToTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as ClassificationDetailsTab;
        this.typologyApiService.getClassificationCommonInfo(this.subtype.toUpperCase() as IncidentSubtype)
            .pipe(rxSpinner('initial'))
            .subscribe(response => {
                this.form = this.formBuilder.group({
                    description: new FormControl(response?.description,
                        [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
                    implementationForm: new FormControl(response?.implementationForm,
                        [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
                    implementationMethod: new FormControl(response?.implementationMethod ? JSON.parse(response.implementationMethod) : null,
                        [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
                    recommendations: new FormControl(response?.recommendations,
                        [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
                });
            });
    }

    breadcrumbCustomizer = (path: string): string => {
        return this.getTextFromDictionary(path);
    };

    specialFilter = (path: string): boolean => {
        return this.subtype === path.toUpperCase();
    };

    cancel(): void {
        this.mainContainer.breadcrumbs.navigateBack();
    }

    publish(): void {
        this.typologyApiService.updateClassificationCommonInfo({
            description: this.form.controls.description.value,
            implementationForm: this.form.controls.implementationForm.value,
            implementationMethod: JSON.stringify(this.form.controls.implementationMethod.value),
            recommendations: this.form.controls.recommendations.value,
            type: this.subtype.toUpperCase() as IncidentSubtype,
        })
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageDataPublished', 'success');
                this.routerService.navigate(
                    Path.ANALYTICS_CLASSIFICATIONS_FRAUD_DETAILS,
                    {
                        pathParams: {
                            [CLASSIFICATION_TYPE]: this.type.toLowerCase(),
                            [CLASSIFICATION_SUBTYPE]: this.subtype.toLowerCase(),
                        },
                        queryParams: {
                            [TAB]: this.type,
                            [NESTED_TAB]: this.backToTab,
                        },
                        queryParamsHandling: 'merge',
                    });
            });
    }

    private getTextFromDictionary(code: string): string {
        if (this.type.toUpperCase() === ClassificationType.FRAUD_TYPE) {
            return '...';
        }
        return code;
    }
}

export interface ClassificationForm {
    description: FormControl<string>;
    implementationForm: FormControl<string>;
    implementationMethod: FormControl<string>;
    recommendations: FormControl<string>;
}
